import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  FiX,
  FiSearch,
  FiShield,
  FiAlertCircle,
  FiCheckCircle,
  FiUser,
  FiRefreshCw,
  FiCheckSquare,
  FiSquare,
} from "react-icons/fi";
import { userRequest } from "../utils/requestMethods";
import { toast } from "react-toastify";
import { formatCurrency } from "../utils/currencyUtils";
import SkeletonTable from "./common/SkeletonTable";

const StudentLoginAccessModal = ({ isOpen, onClose, school, initialClassId = "" }) => {
  const schoolId = school?._id;
  const schoolTerm = school?.settings?.current_term || 1;
  const schoolSession = school?.settings?.current_session || "2024/2025";
  const threshold = school?.settings?.result?.parent_payment_threshold || 0;

  const [students, setStudents] = useState([]);
  const [counts, setCounts] = useState({ total: 0, blocked: 0, exempted: 0, eligible: 0 });
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(initialClassId);
  const [activeTab, setActiveTab] = useState("all"); // 'all', 'blocked', 'exempted', 'eligible'
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    totalPages: 1,
    totalCount: 0,
  });

  // Debounce search input for performance
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPagination((p) => ({ ...p, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch classes list for filtering. The endpoint paginates (default limit 10)
  // and returns { classes: [...] } flat (not wrapped in a `.data` envelope), so
  // both the limit and the correct field must be handled explicitly here.
  useEffect(() => {
    if (!isOpen || !schoolId) return;
    const fetchClasses = async () => {
      try {
        const { data } = await userRequest.get(`/classes/school/${schoolId}?limit=500`);
        setClasses(data?.classes || []);
      } catch (err) {
        console.error("Error fetching classes:", err);
      }
    };
    fetchClasses();
  }, [isOpen, schoolId]);

  // Fetch students login access data
  const fetchLoginAccessData = useCallback(async () => {
    if (!isOpen || !schoolId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
        status: activeTab,
        term: schoolTerm.toString(),
        session: schoolSession,
      });
      if (selectedClassId) params.append("class_id", selectedClassId);
      if (debouncedSearch.trim()) params.append("search", debouncedSearch.trim());

      const { data } = await userRequest.get(
        `/children/schools/${schoolId}/login-access?${params.toString()}`,
      );
      const res = data?.data || data;

      setStudents(res.students || []);
      setCounts(res.counts || { total: 0, blocked: 0, exempted: 0, eligible: 0 });
      setPagination((p) => ({
        ...p,
        totalPages: res.total_pages ?? 1,
        totalCount: res.total_documents ?? 0,
      }));
    } catch (err) {
      console.error("Error fetching student login access:", err);
      toast.error("Failed to load login access data");
    } finally {
      setLoading(false);
    }
  }, [
    isOpen,
    schoolId,
    selectedClassId,
    activeTab,
    debouncedSearch,
    pagination.page,
    pagination.limit,
    schoolTerm,
    schoolSession,
  ]);

  useEffect(() => {
    fetchLoginAccessData();
  }, [fetchLoginAccessData]);

  // Clear selections when changing tab, filters, or page — a selection made on
  // one page must never silently carry over and be acted on for a different page.
  useEffect(() => {
    setSelectedStudentIds([]);
  }, [activeTab, selectedClassId, debouncedSearch, pagination.page]);

  if (!isOpen) return null;

  // Single student toggle with optimistic UI update
  const handleToggleSingle = async (student) => {
    const isCurrentlyExempted = student.login_status === "exempted";
    const newEnabled = !isCurrentlyExempted;

    // Optimistic UI update
    setStudents((prev) =>
      prev.map((s) => {
        if (s._id === student._id) {
          return {
            ...s,
            login_status: newEnabled
              ? "exempted"
              : s.paid_percentage < threshold
                ? "blocked"
                : "eligible",
            is_exempted: newEnabled,
          };
        }
        return s;
      }),
    );

    try {
      await userRequest.post(`/children/schools/${schoolId}/bulk-bypass-payment-threshold`, {
        student_ids: [student._id],
        enabled: newEnabled,
        term: Number(schoolTerm),
        session: schoolSession,
      });

      toast.success(
        newEnabled
          ? `Login exemption granted for ${student.firstname}`
          : `Login exemption revoked for ${student.firstname}`,
      );
      // Re-fetch to update accurate counts
      fetchLoginAccessData();
    } catch (err) {
      toast.error("Failed to update exemption status");
      // Revert on error
      fetchLoginAccessData();
    }
  };

  // Bulk toggle
  const handleBulkToggle = async (enable) => {
    if (!selectedStudentIds.length) return;
    setActionLoading(true);
    try {
      await userRequest.post(`/children/schools/${schoolId}/bulk-bypass-payment-threshold`, {
        student_ids: selectedStudentIds,
        enabled: enable,
        term: Number(schoolTerm),
        session: schoolSession,
      });

      toast.success(
        enable
          ? `Login exemption granted for ${selectedStudentIds.length} student(s)`
          : `Login exemption revoked for ${selectedStudentIds.length} student(s)`,
      );
      setSelectedStudentIds([]);
      fetchLoginAccessData();
    } catch (err) {
      toast.error("Failed to update bulk exemptions");
    } finally {
      setActionLoading(false);
    }
  };

  // Selects only the rows on the currently loaded page — see handleSelectAllMatching
  // for selecting every student that matches the active filter across all pages.
  const handleSelectAll = () => {
    if (selectedStudentIds.length === students.length) {
      setSelectedStudentIds([]);
    } else {
      setSelectedStudentIds(students.map((s) => s._id));
    }
  };

  // Fetches every student id matching the current tab/class/search filter (not just
  // the current page) so a bulk action can genuinely apply to "all blocked students",
  // not silently only the ~20 visible on screen.
  const handleSelectAllMatching = async () => {
    if (!schoolId || pagination.totalCount === 0) return;
    setActionLoading(true);
    try {
      const params = new URLSearchParams({
        page: "1",
        limit: String(Math.min(pagination.totalCount, 500)),
        status: activeTab,
        term: schoolTerm.toString(),
        session: schoolSession,
      });
      if (selectedClassId) params.append("class_id", selectedClassId);
      if (debouncedSearch.trim()) params.append("search", debouncedSearch.trim());

      const { data } = await userRequest.get(
        `/children/schools/${schoolId}/login-access?${params.toString()}`,
      );
      const res = data?.data || data;
      const allIds = (res.students || []).map((s) => s._id);
      setSelectedStudentIds(allIds);

      if (pagination.totalCount > 500) {
        toast.warning(
          `Selected the first 500 of ${pagination.totalCount} matching students — narrow your filters to select the rest.`,
        );
      }
    } catch (err) {
      toast.error("Failed to select all matching students");
    } finally {
      setActionLoading(false);
    }
  };

  const handleSelectStudent = (id) => {
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-3 sm:p-6 animate-fadeIn">
      <div className="bg-white rounded-3xl w-full max-w-5xl h-[90vh] max-h-[850px] flex flex-col shadow-2xl overflow-hidden border border-slate-100">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-800">Student CBT & Login Access</h2>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-indigo-100 text-indigo-700">
                Threshold: {threshold}%
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Term {schoolTerm} • Session {schoolSession} — Manage student test and portal login
              eligibility
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar & Filters */}
        <div className="px-6 py-3.5 bg-white border-b border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          {/* Status Tabs */}
          <div className="flex items-center gap-1.5 overflow-x-auto w-full sm:w-auto pb-1 sm:pb-0">
            <button
              onClick={() => {
                setActiveTab("all");
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                activeTab === "all"
                  ? "bg-slate-800 text-white shadow-sm"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              All Students ({counts.total})
            </button>
            <button
              onClick={() => {
                setActiveTab("blocked");
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                activeTab === "blocked"
                  ? "bg-rose-600 text-white shadow-sm shadow-rose-200"
                  : "bg-rose-50 text-rose-700 hover:bg-rose-100"
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
              Blocked ({counts.blocked})
            </button>
            <button
              onClick={() => {
                setActiveTab("exempted");
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                activeTab === "exempted"
                  ? "bg-purple-600 text-white shadow-sm shadow-purple-200"
                  : "bg-purple-50 text-purple-700 hover:bg-purple-100"
              }`}
            >
              Exempted ({counts.exempted})
            </button>
            <button
              onClick={() => {
                setActiveTab("eligible");
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap ${
                activeTab === "eligible"
                  ? "bg-emerald-600 text-white shadow-sm shadow-emerald-200"
                  : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              }`}
            >
              Eligible ({counts.eligible})
            </button>
          </div>

          {/* Search & Class Dropdown */}
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="relative flex-1 sm:w-48">
              <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
              <input
                type="text"
                placeholder="Search student or Reg ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
              />
            </div>

            <select
              value={selectedClassId}
              onChange={(e) => {
                setSelectedClassId(e.target.value);
                setPagination((p) => ({ ...p, page: 1 }));
              }}
              className="text-xs py-1.5 px-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 text-slate-700"
            >
              <option value="">All Classes</option>
              {classes.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.class_name}
                </option>
              ))}
            </select>

            <button
              onClick={fetchLoginAccessData}
              className="p-2 text-slate-500 hover:text-indigo-600 rounded-xl hover:bg-slate-100 transition-colors"
              title="Refresh"
            >
              <FiRefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>

        {/* Bulk Action Bar (When selected) */}
        {selectedStudentIds.length > 0 && (
          <div className="px-6 py-2.5 bg-indigo-50 border-b border-indigo-100 flex items-center justify-between animate-fadeIn">
            <span className="text-xs font-semibold text-indigo-900 flex items-center gap-2">
              {selectedStudentIds.length} student{selectedStudentIds.length > 1 ? "s" : ""} selected
              {selectedStudentIds.length === students.length &&
                pagination.totalCount > students.length && (
                  <button
                    onClick={handleSelectAllMatching}
                    disabled={actionLoading}
                    className="text-indigo-600 hover:text-indigo-800 underline font-semibold disabled:opacity-50"
                  >
                    Select all {pagination.totalCount} matching students
                  </button>
                )}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleBulkToggle(true)}
                disabled={actionLoading}
                className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors"
              >
                Grant Exemption for Current Term
              </button>
              <button
                onClick={() => handleBulkToggle(false)}
                disabled={actionLoading}
                className="px-3 py-1 bg-white hover:bg-slate-100 border border-slate-300 disabled:opacity-50 text-slate-700 text-xs font-semibold rounded-lg shadow-sm transition-colors"
              >
                Revoke Exemption
              </button>
            </div>
          </div>
        )}

        {/* Table Content */}
        <div className="flex-1 overflow-y-auto">
          {loading && !students.length ? (
            <div className="p-8">
              <SkeletonTable rows={5} columns={7} />
            </div>
          ) : !students.length ? (
            <div className="flex flex-col items-center justify-center h-64 text-center p-6">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 mb-3">
                <FiUser className="w-6 h-6" />
              </div>
              <p className="text-sm font-semibold text-slate-700">No students found</p>
              <p className="text-xs text-slate-400 mt-1 max-w-xs">
                {activeTab === "blocked"
                  ? "Great! There are no blocked students for this selection."
                  : "No student records match your current filters."}
              </p>
            </div>
          ) : (
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50/50 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                  <th className="py-3 pl-6 pr-3 w-10">
                    <button
                      onClick={handleSelectAll}
                      className="text-slate-400 hover:text-slate-700"
                    >
                      {selectedStudentIds.length === students.length && students.length > 0 ? (
                        <FiCheckSquare className="w-4 h-4 text-indigo-600" />
                      ) : (
                        <FiSquare className="w-4 h-4" />
                      )}
                    </button>
                  </th>
                  <th className="py-3 px-3">Student</th>
                  <th className="py-3 px-3">Class</th>
                  <th className="py-3 px-3">Fee Paid</th>
                  <th className="py-3 px-3 text-center">Paid %</th>
                  <th className="py-3 px-3 text-center">Login Status</th>
                  <th className="py-3 pr-6 pl-3 text-right">Allow Login</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs">
                {students.map((student) => {
                  const isSelected = selectedStudentIds.includes(student._id);
                  const isExempted = student.login_status === "exempted";
                  const isBlocked = student.login_status === "blocked";

                  return (
                    <tr
                      key={student._id}
                      className={`hover:bg-slate-50/80 transition-colors ${
                        isSelected ? "bg-indigo-50/30" : ""
                      }`}
                    >
                      <td className="py-3 pl-6 pr-3">
                        <button
                          onClick={() => handleSelectStudent(student._id)}
                          className="text-slate-400 hover:text-slate-700"
                        >
                          {isSelected ? (
                            <FiCheckSquare className="w-4 h-4 text-indigo-600" />
                          ) : (
                            <FiSquare className="w-4 h-4" />
                          )}
                        </button>
                      </td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                            {student.picture?.path ? (
                              <img
                                src={student.picture.path}
                                alt={student.firstname}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <span className="font-semibold text-slate-500 text-xs">
                                {student.firstname?.[0]}
                                {student.lastname?.[0]}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-800 flex items-center gap-1.5">
                              {student.lastname} {student.firstname}
                              {student.has_prior_waivers && (
                                <span
                                  title={
                                    student.last_waived_at
                                      ? `Previously waived (most recently on ${new Date(
                                          student.last_waived_at,
                                        ).toLocaleDateString()})`
                                      : "Previously waived"
                                  }
                                  className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                                >
                                  Waived before
                                </span>
                              )}
                            </p>
                            <p className="text-[11px] text-slate-400">{student.registration_id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 font-medium text-slate-600">
                        {student.class_info?.class_name || student.current_class || "—"}
                      </td>
                      <td className="py-3 px-3">
                        <div className="space-y-1">
                          <div className="flex items-center gap-1.5 text-[11px]">
                            <span className="font-semibold text-slate-700">
                              {formatCurrency(student.amount_paid || 0)}
                            </span>
                            <span className="text-slate-400">/</span>
                            <span className="text-slate-500">
                              {formatCurrency(student.total_amount || 0)}
                            </span>
                          </div>
                          {/* Mini Progress Bar */}
                          <div className="w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${
                                (student.paid_percentage || 0) >= threshold
                                  ? "bg-emerald-500"
                                  : "bg-rose-500"
                              }`}
                              style={{ width: `${Math.min(student.paid_percentage || 0, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-3 text-center font-semibold">
                        <span
                          className={`${
                            (student.paid_percentage || 0) >= threshold
                              ? "text-emerald-600"
                              : "text-rose-600"
                          }`}
                        >
                          {student.paid_percentage || 0}%
                        </span>
                      </td>
                      <td className="py-3 px-3 text-center">
                        {isBlocked && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">
                            <FiAlertCircle className="w-3 h-3 text-rose-500" />
                            Blocked
                          </span>
                        )}
                        {isExempted && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-purple-50 text-purple-700 border border-purple-200">
                            <FiShield className="w-3 h-3 text-purple-500" />
                            Exempted
                          </span>
                        )}
                        {!isBlocked && !isExempted && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <FiCheckCircle className="w-3 h-3 text-emerald-500" />
                            Eligible
                          </span>
                        )}
                      </td>
                      <td className="py-3 pr-6 pl-3 text-right">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!isBlocked}
                            onChange={() => handleToggleSingle(student)}
                            disabled={!isBlocked && !isExempted} // Disable if student naturally meets threshold
                            className="sr-only peer"
                          />
                          <div
                            className={`w-9 h-5 rounded-full peer peer-focus:outline-none transition-all after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all ${
                              !isBlocked && !isExempted
                                ? "bg-emerald-400 opacity-60 cursor-not-allowed after:translate-x-full"
                                : isExempted
                                  ? "bg-[#3F3DA5] after:translate-x-full"
                                  : "bg-gray-300"
                            }`}
                          ></div>
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer & Pagination */}
        <div className="px-6 py-3.5 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
          <div>
            Showing <span className="font-semibold text-slate-700">{students.length}</span> of{" "}
            <span className="font-semibold text-slate-700">{pagination.totalCount}</span> students
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => setPagination((p) => ({ ...p, page: Math.max(p.page - 1, 1) }))}
              disabled={pagination.page <= 1 || loading}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-slate-700 transition-colors"
            >
              Previous
            </button>
            <span className="px-2 font-medium">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              onClick={() =>
                setPagination((p) => ({ ...p, page: Math.min(p.page + 1, pagination.totalPages) }))
              }
              disabled={pagination.page >= pagination.totalPages || loading}
              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed font-medium text-slate-700 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentLoginAccessModal;
